import { addMonths } from "date-fns"
import { z } from "zod"

import { config } from "@/config"
import {
  caseIdSchema,
  datetimeSchema,
  idSchema,
  optionalDateTimeSchema,
} from "@/helpers/zodSchemas"

namespace Encoder {
  export const defaultValues = (rawCallReport: unknown) => {
    try {
      return Decoder.schema.parse(rawCallReport)
    } catch {
      return { status: "", reason: "", scheduledCallDate: null, memo: "" }
    }
  }
}

namespace Decoder {
  export const schema = z
    .object({
      status: z.string(),
      reason: z.string(),
      scheduledCallDate: datetimeSchema.nullable(),
      memo: z.string(),
      updated_at: optionalDateTimeSchema,
    })
    .superRefine(({ scheduledCallDate }, ctx) => {
      const minDate = new Date()
      const maxDate = addMonths(minDate, config.maxScheduledCallMonths)

      if (scheduledCallDate && scheduledCallDate < new Date()) {
        ctx.addIssue({
          code: "too_small",
          origin: "date",
          minimum: minDate.getTime(),
          inclusive: true,
          input: scheduledCallDate,
          error: "Scheduled call date is too far in the past",
          path: ["scheduledCallDate"],
        })
      }

      if (scheduledCallDate && scheduledCallDate > maxDate) {
        ctx.addIssue({
          code: "too_big",
          origin: "date",
          maximum: maxDate.getTime(),
          inclusive: true,
          input: scheduledCallDate,
          error: "Scheduled call date is too far in the future",
          path: ["scheduledCallDate"],
        })
      }
    })

  export type DecoderType = z.infer<typeof schema>

  export const saveReportSchema = z.object({
    data: z.object({
      case_id: caseIdSchema,
      is_campaign_no_call: z.boolean(),
      is_global_no_call: z.boolean().nullable(),
      new_contact_id: idSchema.nullable(),
    }),
    reqId: idSchema,
    success: z.string(),
  })

  export type SaveReportResult = z.infer<typeof saveReportSchema>
}

export { Decoder, Encoder }
